import type { ReactNode } from "react";
import { WIDE_WIDTHS, srcSetFor } from "../lib/images";

// B&W engagement photos taken on the Metro — the banner for the Travel page.
// Landscape shots crop hard when squeezed into thirds of a phone, so we reveal
// them progressively: 1 on mobile, 2 on small screens, all 3 once there's room.
// `reveal` is the breakpoint at which each extra photo appears. The two that
// start hidden are also `lazy`: a lazy image that never intersects the viewport
// is never fetched, so a phone skips ~460KB it would otherwise download and
// never show. The first frame is above the fold and must NOT be lazy.
const TRANSIT_BANNER = [
  { src: "/travel/transit-2.webp", reveal: "", lazy: false },
  { src: "/travel/transit-mid.webp", reveal: "hidden sm:block", lazy: true },
  { src: "/travel/transit-1.webp", reveal: "hidden lg:block", lazy: true },
];

// Hilton room-block link: property RICWSHF (Short Pump), group 91O, dates
// pre-filled Apr 30 - May 2 2027. If it's ever cleared, the Hotels section
// falls back to a "coming soon" line rather than shipping a dead button.
const ROOM_BLOCK_URL =
  "https://www.hilton.com/en/book/reservation/rooms/?ctyhocn=RICWSHF&arrivalDate=2027-04-30&departureDate=2027-05-02&groupCode=91O&room1NumAdults=1&cid=OM%2CWW%2CHILTONLINK%2CEN%2CDirectLink";

export function Travel() {
  return (
    <div className="pb-24">
      {/* Full-width photo banner, flush to the nav (cancels the page's top
          padding). Add more paths to TRANSIT_BANNER to grow the strip. */}
      <div className="relative left-1/2 -mt-12 flex w-screen -translate-x-1/2 gap-1 overflow-hidden border-t border-midnight-teal/25 bg-cream">
        {TRANSIT_BANNER.map(({ src, reveal, lazy }) => (
          <img
            key={src}
            src={src}
            srcSet={srcSetFor(src, WIDE_WIDTHS, 1600)}
            // One frame spans the viewport on a phone, two at sm, three at lg.
            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            alt="Karishma and Ben"
            loading={lazy ? "lazy" : "eager"}
            decoding="async"
            className={`h-56 min-w-0 flex-1 object-cover sm:h-72 md:h-80 ${reveal}`}
          />
        ))}
      </div>

      <div className="mx-auto max-w-2xl px-6">
        <header className="mt-14 text-center">
          <h1 className="font-serif text-5xl text-pine sm:text-6xl">Travel</h1>
          <p className="mx-auto mt-5 font-sans text-lg font-normal tracking-[0.02em] text-pine">
            All wedding events will take place at the Estate at River Run (2421
            River Road W, Maidens, Virginia, 23102).
          </p>
        </header>

        <Section title="Hotels">
          <p>
            We have secured a special rate at the Hilton Richmond Hotel &amp;
            Spa/Short Pump (12042 West Broad Street, Richmond VA 23233),
            approximately 30 minutes from the Estate at River Run. These rooms
            are first come, first served &mdash; please use the link below to
            book directly within the room block.
          </p>
          {/* Centered in its own row — the placeholder sits in the same slot,
              so adding the URL later doesn't shift the layout. */}
          <div className="mt-7 text-center">
            {ROOM_BLOCK_URL ? (
              <a
                href={ROOM_BLOCK_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-block bg-midnight-teal px-10 py-3 font-sans text-base font-medium uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pine"
              >
                Book a hotel room
              </a>
            ) : (
              <p className="font-sans text-base italic text-pine/55">
                Booking link coming soon.
              </p>
            )}
          </div>
        </Section>

        <Section title="Transportation to events">
          <p>
            Shuttle service will be available to and from our room block hotel
            for Friday and Saturday evening &mdash; more details on shuttle
            schedules and logistics will be shared closer to the wedding.
          </p>
        </Section>

        <Section title="Flights">
          <p>
            For those flying in, we recommend Richmond International Airport
            (RIC), located about 30 minutes away from our hotel block. You are
            also welcome to fly into Washington Dulles International Airport
            (IAD) or Ronald Reagan Washington National Airport (DCA), both
            roughly 2 hours from Richmond.
          </p>
        </Section>

        <Section title="Train">
          <p>
            If you are traveling by train, we recommend either Richmond Staples
            Mill Road Station (RVR), about 20 minutes from our hotel block, or
            Richmond Main Street Station (RVM), about 25&ndash;30 minutes away.
            Both stations service the Amtrak Northeast Regional line that runs
            between Washington D.C., New York City, and Richmond.
          </p>
        </Section>
      </div>
    </div>
  );
}

/**
 * One titled block, separated from the last by a hairline rule and a single
 * sage-moss diamond — the site's print vocabulary, not a filled card.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-14">
      <div aria-hidden="true" className="flex items-center gap-3">
        <span className="h-px flex-1 bg-pine/15" />
        <span className="inline-block h-1.5 w-1.5 rotate-45 bg-sage-moss" />
        <span className="h-px flex-1 bg-pine/15" />
      </div>
      <h2 className="mt-8 font-serif text-3xl text-pine sm:text-4xl">
        {title}
      </h2>
      <div className="mt-4 font-sans text-lg font-normal leading-relaxed tracking-[0.02em] text-pine">
        {children}
      </div>
    </section>
  );
}
